import { LoadingOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import promiseResolver from '../../../../lib/util/promiseResolver';

const lxButtonStyle = css`
  --bt-lg-height: 48px;
  --bt-lg-padding: 15px 30px;
  --bt-md-height: 40px;
  --bt-md-padding: 11px 20px;
  --bt-sm-height: 32px;
  --bt-sm-padding: 7px 16px;
  --bt-sm-font-size: 13px;
  --bt-sm-line-height: 17px;
  --bt-border: 1px solid ${(props) => props.theme.colors.primary500};
  --bt-low-border: 1px solid ${(props) => props.theme.colors.grayLine};

  --bt-primary: ${(props) => props.theme.colors.white};
  --bt-primary-bg: ${(props) => props.theme.colors.primary500};
  --bt-primary-hover: #950934;
  --bt-low-primary: ${(props) => props.theme.colors.white};
  --bt-low-primary-bg: ${(props) => props.theme.colors.grayIcon};
  --bt-low-primary-hover: ${(props) => props.theme.colors.title};

  --bt-secondary: ${(props) => props.theme.colors.primary500};
  --bt-secondary-bg: ${(props) => props.theme.colors.white};
  --bt-secondary-hover: ${(props) => props.theme.colors.primaryBg};
  --bt-low-secondary: ${(props) => props.theme.colors.body};
  --bt-low-secondary-bg: ${(props) => props.theme.colors.white};
  --bt-low-secondary-hover: ${(props) => props.theme.colors.grayBgLight};

  --bt-sm-secondary: ${(props) => props.theme.colors.primary500};
  --bt-sm-secondary-bg: ${(props) => props.theme.colors.primaryBg};
  --bt-sm-secondary-hover: #ffdfe8;
  --bt-sm-border-radius: 18px;
`;

interface LXButtonProps {
  width?: number | string;
  children?: React.ReactNode;
  kind?: 'primary' | 'secondary';
  onClick?:
    | (() => Promise<void>)
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
      ) => Promise<void>);
  alignSelf?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  disabled?: boolean;
  tabIndex?: number;
  refProp?: React.RefObject<HTMLButtonElement>;
  position?: 'absolute' | 'relative';
  bottom?: number;
  disabledColor?: string;
  fontSize?: string;
}

const StyledButton = styled.button<LXButtonProps>`
  ${lxButtonStyle}
  width: ${(props) =>
    typeof props.width === 'string' ? props.width : `${props.width}px`};
  border: 0;
  tabindex: ${(props) => props.tabIndex};
  align-self: ${(props) => props.alignSelf};
  position: ${(props) => props.position};
  bottom: ${(props) => props.bottom}px;
  color: var(--bt-primary);
  background-color: var(--bt-primary-bg);
  &:hover {
    background-color: var(--bt-primary-hover);
    cursor: pointer;
  }
  ${(props) =>
    props.kind === 'secondary' &&
    css`
      color: var(--bt-secondary);
      background-color: var(--bt-secondary-bg);
      &:hover {
        background-color: var(--bt-secondary-hover);
      }
      border: var(--bt-border);
    `}
  &:disabled {
    cursor: default;
    color: ${(props) => props.disabledColor};
    opacity: 0.4;
  }

  border-radius: 24px;
  font-family: Outfit;
  font-size: ${(props) => props.fontSize ?? '14px'};
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0em;
  text-align: center;
`;

const LXStyledButton = (props: LXButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const onClick = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (props.onClick) {
      const ret = props.onClick(event);
      if (ret instanceof Promise) {
        promiseResolver<void>({
          promise: ret,
          isLoadingCallback: setIsLoading,
        });
      }
    }
  };

  return (
    <StyledButton
      {...props}
      ref={props.refProp}
      disabled={isLoading ? isLoading : props.disabled}
      children={isLoading ? <LoadingOutlined /> : props.children}
      onClick={onClick}
    ></StyledButton>
  );
};

export default LXStyledButton;
